// TODO: VL-6033 - re-write the vl_chat FE by FE engineers.

import React, { createContext, useContext, useEffect, useState } from 'react';
import { CopilotKit, useCoAgent } from '@copilotkit/react-core';
import { v4 as uuidv4 } from 'uuid';
import VLChatNavigateAction from '../VLChatNavigateAction';

/**
 * Helper function to get CopilotKit configuration props
 * @param userToken - User authentication token
 * @param datasetId - Current dataset ID
 * @param threadId - Chat thread ID
 * @returns CopilotKit configuration object
 */
const getCopilotKitProps = (
  userToken: string | undefined,
  datasetId: string | null,
  threadId: string | undefined
) => {
  return {
    publicApiKey: process.env.REACT_APP_COPILOTKIT_PUBLIC_KEY,
    agent: "vl_chat",
    threadId: threadId,
    showDevConsole: true,
    properties: {
      authorization: userToken,
      dataset_id: datasetId,
    }
  };
};

/**
 * Thread management context for VL Chat
 * Provides thread ID and thread creation functionality to child components
 */
interface ThreadContextType {
  threadId: string | undefined;
  createNewThread: () => void;
  datasetId: string | null;
}

const ThreadContext = createContext<ThreadContextType | undefined>(undefined);

/**
 * Hook to access thread management functionality
 * Must be used within VLChatWrapper
 */
export const useVLChatThread = () => {
  const context = useContext(ThreadContext);
  if (!context) {
    throw new Error('useVLChatThread must be used within VLChatWrapper');
  }
  return context;
};

/**
 * Agent state wrapper component
 * Manages CopilotKit agent state for dataset context
 */
const AgentStateProvider = ({ 
  children, 
  datasetId, 
  userInfo 
}: { 
  children: React.ReactNode;
  datasetId: string | null;
  userInfo: any;
}) => {
  // Agent state management
  type AgentState = {
    dataset_id?: string;
    user_id?: string;
  };

  const { state, setState } = useCoAgent<AgentState>({
    name: "vl_chat",
    initialState: {
      dataset_id: datasetId || undefined,
      user_id: userInfo?.userId || userInfo?.email || undefined,
    },
  });

  // Extract stable user ID
  const userId = userInfo?.userId || userInfo?.email || undefined;
  
  // Update agent state when dataset or user changes
  useEffect(() => {
    // Only update if values actually changed
    if (state?.dataset_id !== datasetId || state?.user_id !== userId) {
      setState({
        dataset_id: datasetId || undefined,
        user_id: userId,
      });
    }
  }, [datasetId, userId, state?.dataset_id, state?.user_id, setState]);

  return <>{children}</>;
};

/**
 * VL Chat Wrapper Component
 * 
 * This component encapsulates ALL VL Chat functionality including:
 * - Thread management (with context)
 * - CopilotKit provider
 * - Agent state management
 * - Navigation action
 * 
 * This keeps all VL Chat logic contained and makes it easy to disable
 * the entire feature by commenting out this wrapper in App.tsx
 */
interface VLChatWrapperProps {
  children: React.ReactNode;
  datasetId: string | null;
  userInfo: any;
  userToken: string | undefined;
  isEnabled?: boolean; // Optional flag to disable VL Chat
}

export const VLChatWrapper: React.FC<VLChatWrapperProps> = ({
  children,
  datasetId,
  userInfo,
  userToken,
  isEnabled = true
}) => {

  // Get or create thread ID for the current dataset
  const getThreadId = (datasetId: string | null) => {
    const threadIdKey = datasetId 
      ? `copilotkit_thread_id_${datasetId}` 
      : 'copilotkit_thread_id_no_dataset';
    
    let id = sessionStorage.getItem(threadIdKey);
    
    if (!id) {
      id = uuidv4();
      sessionStorage.setItem(threadIdKey, id);
    }
    
    return id;
  };

  // Thread management state - Initialize with current dataset's thread
  const [threadId, setThreadId] = useState<string>(() => getThreadId(datasetId));
  
  // Update thread ID when dataset changes
  useEffect(() => {
    const newThreadId = getThreadId(datasetId);
    
    if (newThreadId !== threadId) {
      setThreadId(newThreadId);
    }
  }, [datasetId]); // Removed threadId from deps to avoid infinite loop

  // Function to create new thread
  const createNewThread = () => {
    const threadIdKey = datasetId 
      ? `copilotkit_thread_id_${datasetId}` 
      : 'copilotkit_thread_id_no_dataset';
    
    const newId = uuidv4();
    sessionStorage.setItem(threadIdKey, newId);
    setThreadId(newId);
  };

  // If VL Chat is disabled, just render children
  if (!isEnabled) {
    return <>{children}</>;
  }

  // Get CopilotKit props
  const copilotKitProps = getCopilotKitProps(userToken, datasetId, threadId);

  return (
    <CopilotKit 
      {...copilotKitProps}
    >
      <ThreadContext.Provider value={{ threadId, createNewThread, datasetId }}>
        <AgentStateProvider datasetId={datasetId} userInfo={userInfo}>
          <VLChatNavigateAction />
          {children}
        </AgentStateProvider>
      </ThreadContext.Provider>
    </CopilotKit>
  );
};

export default VLChatWrapper;